import { datadogLogs } from '@datadog/browser-logs'
import { POST_AUTH_ROUTING_STORAGE_KEY } from '@/features/auth/helpers/auth-constants'
import { UrlConfig } from '@/setup/routing/routing'
import { buildMcpOAuthBrowserLogFields } from '@/utils/clerk/mcp-oauth-observability'

export function resolvePostAuthRedirectUrl(oauthRedirectUrl: string | null): string {
    const target = oauthRedirectUrl || UrlConfig['/routing'].getUrl()
    if (target.startsWith('http://') || target.startsWith('https://')) {
        return target
    }
    if (typeof window === 'undefined') {
        return target
    }
    return new URL(target, window.location.origin).href
}

function getPostAuthPendingNavigation(redirectUrl: string): string {
    const target = new URL(redirectUrl, window.location.origin)
    return target.origin === window.location.origin
        ? target.pathname + target.search
        : target.href
}

function isOnPostAuthTarget(redirectUrl: string): boolean {
    const target = new URL(redirectUrl, window.location.origin)
    if (target.origin !== window.location.origin) {
        return false
    }
    return window.location.pathname.startsWith(target.pathname)
}

/** Navigate after landing-modal OTP when sessionStorage still holds a pending post-auth path. */
export function completePendingPostAuthNavigation(pending: string): void {
    if (pending.startsWith('http://') || pending.startsWith('https://')) {
        window.location.assign(pending)
        return
    }
    window.location.assign(new URL(pending, window.location.origin).href)
}

type SetActiveFn = (params: { session: string; redirectUrl: string }) => Promise<unknown>

export async function completePostAuthNavigation(
    setActive: SetActiveFn | undefined,
    sessionId: string,
    oauthRedirectUrl: string | null
) {
    const redirectUrl = resolvePostAuthRedirectUrl(oauthRedirectUrl)

    sessionStorage.setItem(POST_AUTH_ROUTING_STORAGE_KEY, getPostAuthPendingNavigation(redirectUrl))

    const activationFields = buildMcpOAuthBrowserLogFields(redirectUrl, 'session_activation')
    if (activationFields) datadogLogs.logger.info('mcp_oauth.browser_stage', activationFields)
    await setActive?.({ session: sessionId, redirectUrl })

    if (!isOnPostAuthTarget(redirectUrl)) {
        const navigationFields = buildMcpOAuthBrowserLogFields(redirectUrl, 'fallback_navigation')
        if (navigationFields) datadogLogs.logger.info('mcp_oauth.browser_stage', navigationFields)
        window.location.assign(redirectUrl)
    }
}
